import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Unified i18n bootstrap (Task 30).
 *
 * Every `src/i18n/locales/{lang}/{namespace}.json` is auto-discovered at
 * build time via `import.meta.glob` and registered as a `{lang, namespace}`
 * resource bundle. Adding a new namespace is now a one-file operation:
 * drop `locales/fr/foo.json` + `locales/en/foo.json`, restart the dev
 * server, done — no edits to this file, no `addResourceBundle` calls
 * scattered across page modules.
 *
 * Vite (and Vitest, which runs on Vite) supports `import.meta.glob` with
 * `{ eager: true }`, so the JSON modules are inlined into the bundle and
 * synchronously available before `i18n.init` is called.
 */

interface LocaleModule {
  default: Record<string, unknown>;
}

// `eager: true` materialises every match at module-load time (no async
// imports, no dynamic fetch). Path is relative to THIS file.
const modules = import.meta.glob<LocaleModule>('./locales/*/*.json', {
  eager: true,
});

type Resources = Record<string, Record<string, Record<string, unknown>>>;

const PATH_RE = /\.\/locales\/(fr|en)\/(.+)\.json$/;

const builtResources: Resources = {};
const builtNamespaces = new Set<string>();

for (const [path, mod] of Object.entries(modules)) {
  const match = PATH_RE.exec(path);
  if (!match) continue;
  const lang = match[1] as 'fr' | 'en';
  const ns = match[2] as string;
  if (!builtResources[lang]) {
    builtResources[lang] = {};
  }
  builtResources[lang][ns] = mod.default;
  builtNamespaces.add(ns);
}

export const namespaces: ReadonlyArray<string> = Array.from(
  builtNamespaces,
).sort();
export const defaultNS = 'common';

export const STORAGE_KEY = 'displaycms.lang';

/**
 * Computes the initial language from (in priority order):
 *   1. `?lang=fr|en` URL query param — persists the choice in localStorage.
 *   2. previously-persisted localStorage value.
 *   3. fallback to French.
 *
 * Exported for unit-testability; also called at module-load to seed
 * `i18n.init({ lng })`.
 */
export function detectInitialLang(): string {
  if (typeof window === 'undefined') return 'fr';
  try {
    const url = new URL(window.location.href);
    const fromQuery = url.searchParams.get('lang');
    if (fromQuery === 'fr' || fromQuery === 'en') {
      window.localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const fromStorage = window.localStorage.getItem(STORAGE_KEY);
    if (fromStorage === 'fr' || fromStorage === 'en') return fromStorage;
  } catch {
    // ignore — fall through to default
  }
  return 'fr';
}

/**
 * Returns true when the user has an explicit, persisted locale preference
 * (set via `?lang=` or via the in-app switcher). Used by the auth bootstrap
 * to decide whether `/auth/me`'s `user.locale` should override the current
 * language. Spec §6: "auth response sets initial lang" — but a user who
 * actively flipped the switcher should not have it reverted on next refresh.
 */
export function hasExplicitLangPreference(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'fr' || stored === 'en';
  } catch {
    return false;
  }
}

export function persistLang(lang: 'fr' | 'en'): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // ignore — best-effort
  }
}

void i18n.use(initReactI18next).init({
  resources: builtResources,
  lng: detectInitialLang(),
  fallbackLng: 'fr',
  defaultNS,
  ns: namespaces as string[],
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

export default i18n;
